import { inject, Injectable } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { withHistory } from '@sama/rxjs-core';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import {
  MouseButton,
  MouseEventInfo,
} from '../../../cross-team-shared/misc-services/events/mouse-event.service';
import { EditMode3D } from '../../../cross-team-shared/types/tool-modes';
import { MainCameraService } from '../../render/cameras/main-camera.service';
import { TransformControls } from '../../render/controls/TransformControls';
import { GlobalRenderTriggerService } from '../../render/global-render-trigger.service';
import {
  CuboidFace,
  CuboidHelper,
} from '../../render/mesh/cuboid/cuboid-helper';
import { CuboidService } from '../../render/mesh/cuboid/cuboid.service';
import { RendererLayer, setRendererLayers } from '../../render/render-utils';
import { SceneControllerService } from '../../render/scene-controller.service';
import { rotateSelectedFace } from '../../state/cuboid-location-state-3d';
import { PointCloudToolName } from '../../state/point-cloud-content-state';
import { getShapeForEventTracking } from '../../utils/misc-utils';
import { SelectTool3D } from '../select-tool/select-tool-3d';
import { EditSecondaryToolbar3DComponent } from './edit-secondary-toolbar-3d.component';
import { EditToolDragHandler } from './edit-tool-drag-handler';

@Injectable()
export class EditTool3D extends SelectTool3D {
  override readonly name: PointCloudToolName = 'Edit';

  override secondaryToolbarType = EditSecondaryToolbar3DComponent;
  private controls?: any; // should be `TransformControls`. See .d.ts file

  private cuboidService = inject(CuboidService);
  private globalRenderTriggerService = inject(GlobalRenderTriggerService);
  private mainCameraService = inject(MainCameraService);
  private sceneControllerService = inject(SceneControllerService);

  override async onActivate() {
    await super.onActivate();

    this.onEvent('container', 'click', this.onContainerClick, {
      modifiers: ['shiftKey'],
    });

    this.onEvent('container', 'mousedown', this.onContainerLeftMouseDown, {
      button: MouseButton.Left,
      evenWithRegisteredHelper: true,
    });

    this.setUpSubscriptions();
    this.setUpEditModeSubscriptions();
  }

  override onDeactivate() {
    super.onDeactivate();
    this.cleanUpControls();
  }

  setEditMode(mode: EditMode3D) {
    this.store('render')('editMode').set(mode);
    this.setSelectedTool();
  }

  override setMode(mode: string) {
    assert(
      mode === 'select face' || mode === 'translate' || mode === 'rotate',
      'invalid mode',
    );
    this.setEditMode(mode);
  }

  getEditMode() {
    return this.store.state().render.editMode;
  }

  protected override onContainerClick(
    event: MouseEvent,
    eventInfo: MouseEventInfo,
  ) {
    if (this.controls?.isOverControls(event)) {
      return;
    }

    super.onContainerClick(event, eventInfo, 'edit_tool');
    const newEditPanels = this.shellConnector.hasFeatureFlag('new-edit-panel');

    if (this.inSelectFaceMode() && !newEditPanels) {
      this.selectIntersectingCuboidFace(event);
    }
  }

  protected override onContainerMouseMove(event: MouseEvent) {
    this.controls?._onPointerHover(event);
    super.onContainerMouseMove(event);

    if (this.getEditMode() === 'select face' && !this.isDragging) {
      this.previewIntersectingCuboidFace(event);
    }
  }

  protected override setSelectedTool() {
    this.shellConnector.setSelectedTool(this.name, this.getEditMode());
  }

  private onContainerLeftMouseDown(event: MouseEvent) {
    if (this.controls?.isOverControls(event)) {
      this.controls._onPointerDown(event);
      this.registerHelper(
        new EditToolDragHandler(this.controls, event, this.injector),
      );
    }
  }

  private setUpSubscriptions() {
    const visibleCuboid$ = combineLatest([
      this.cuboidService.focusedCuboid$,
      this.store.focusedShapeVisible$,
    ]).pipe(
      map(([cuboid, visible]) => (cuboid && visible ? cuboid : undefined)),
      distinctUntilChanged(),
    );

    const cuboidWithControls$ = combineLatest([
      visibleCuboid$,
      this.store('render')('editMode').$,
    ]).pipe(
      map(([visibleCuboid, editMode]) => {
        if (visibleCuboid && editMode !== 'select face') {
          return visibleCuboid;
        } else {
          return undefined;
        }
      }),
    );

    this.subscribeToWhileActive(cuboidWithControls$, this.rebuildControls);
  }

  private setUpEditModeSubscriptions() {
    this.subscribeTo(
      this.store('render')('editMode').$.pipe(
        distinctUntilChanged(),
        withHistory(1),
      ),
      ([_current, previous]) => {
        if (previous === 'select face') {
          this.cuboidService
            .getHelpers()
            .forEach((cuboid) => cuboid.resetFaceOfInterest());
        }
      },
    );
  }

  private rebuildControls(cuboid?: CuboidHelper) {
    this.cleanUpControls();
    if (cuboid) {
      this.addControls(cuboid);
    }
    this.renderScene();
  }

  private addControls(cuboid: CuboidHelper) {
    // @ts-ignore
    this.controls = new TransformControls(
      this.mainCameraService.camera,
      this.mainCameraService.mainDomElement,
    );
    this.controls.setSpace('local');
    this.controls.attach(cuboid.box);
    this.controls.addEventListener('change', this.renderScene.bind(this));
    this.controls.setMode(this.getEditMode());
    setRendererLayers([RendererLayer.Main], this.controls);
    this.sceneControllerService.addToScene(this.controls);
  }

  private cleanUpControls() {
    if (this.controls) {
      this.controls.detach();
      this.controls.removeEventListener('change', this.renderScene.bind(this));
      this.sceneControllerService.removeFromScene(this.controls);
      this.controls.dispose();
      this.controls = undefined;
    }
  }

  private previewIntersectingCuboidFace(event: MouseEvent) {
    const [cuboid, face] = this.rayCastService.getIntersectingCuboidAndFace(
      event.shiftKey,
    );

    // ensures cuboids are reset when no longer hovered
    this.cuboidService.getHelpers().forEach((c) => c.resetFaceOfInterest());

    if (isDefined(cuboid) && isDefined(face)) {
      cuboid.previewFaceOfInterest(face);
      this.store('render')('highlightedFace').set(face);
    } else {
      this.store('render')('highlightedFace').set(undefined);
    }
  }

  private selectIntersectingCuboidFace(event: MouseEvent) {
    const [cuboid, face] = this.rayCastService.getIntersectingCuboidAndFace(
      event.shiftKey,
    );
    // face can be 0
    if (cuboid && isDefined(face)) {
      this.updateFaceOfInterest(cuboid.index, face);
      const cuboidState = this.store.state().render.cuboids[cuboid.index];
      this.shellConnector.pushAndTrack({
        id: 'edit_shape_points',
        bucket: 'adjustment',
        method: 'select_face',
        shapes: [getShapeForEventTracking(cuboidState)],
      });
    }
  }

  private updateFaceOfInterest(cuboidIndex: number, face: CuboidFace) {
    this.store('render')('cuboids')(cuboidIndex)('key_locations').setUsing(
      (locations) =>
        locations.map((location) => rotateSelectedFace(location, face)),
    );
  }

  private inSelectFaceMode() {
    return this.getEditMode() === 'select face';
  }

  private renderScene() {
    this.globalRenderTriggerService.trigger$.next();
  }
}
